<script setup lang="ts">
import { ref, inject, Ref, onBeforeMount } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { useI18n } from 'vue-i18n';
import { useViewComposable } from '@/views/composable';
import { FilterMatchMode, FilterOperator, FilterService } from '@primevue/core/api';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import ViewRoomExclusions from '@/components/modal/ViewRoomExclusions.vue';

const dialogRef = inject('dialogRef') as Ref;
const onApply = dialogRef?.value?.options?.emits?.onApply;
const onClose = dialogRef?.value?.options?.emits?.onClose;
const hotels = dialogRef?.value?.options.data.hotels;
const sheetData = dialogRef?.value?.options.data.sheetData;
const type = dialogRef?.value?.options.data.type;
const tabType = dialogRef?.value?.options.data.tabType;

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const filters = ref();
const sheetsDataHotel = ref<any>([]);
const sortOrder = ref(0);
const sortField = ref('validateMessage');
const isSort = ref(false);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const { apiOrder, styleColumnExport, renderNameFileExport } = useViewComposable();
const widthList = [20, 12, 50, 50, 20, 20, 20, 20, 10, 20, 40, 20, 10, 20, 20, 20, 10, 10, 10];
const sheetColumnStyle = styleColumnExport(widthList);

FilterService.register('filterDates', (dates, filterValue) => {
  const datesFormat = handleDisplayDate(dates);
  return !filterValue || datesFormat.includes(filterValue);
});

const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    code: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    nameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    chainName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    chainNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destSVId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    hotelType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    hotelTypeHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    statusHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    groupTypes: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    groupTypesHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    roomCategoryCallOrder: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    roomCategoryCallOrderHotel: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'hotelPreferred.sts': { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'hotelPreferredHotel.sts': { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    bookingDate: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    bookingDateHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    travelDate: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    travelDateHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    blackoutDate: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    blackoutDateHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterDates' }] },
    insta: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    instaHotel: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const handleDisplayDate = (listDate: any) => {
  let format = '';
  if (listDate?.length > 0) {
    format = listDate.map((item: any) => `${formatDate(item.start, 'dd-LLL-yyyy', true, currentLanguage)} to ${formatDate(item.end, 'dd-LLL-yyyy', true, currentLanguage)}`).join('\n');
  }
  return format;
};

const handleImportFile = () => {
  onApply();
};

const handleRevert = () => {
  onApply();
};

const closeModal = () => {
  onClose();
};

const renderStatus = (status: string) => {
  return status === STATUS_OPEN ? STATUS_OPEN : STATUS_CLOSED;
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};

const rowClass = (data: any) => {
  return [{ 'bg-error': data.validateMessage }];
};

const handleShowMessage = (message: string) => {
  return message ?? t('common.labels.success');
};

const renderName = () => {
  return tabType === 'PACKAGE' ? 'PKG_Hotel_Management' : 'HTL_Hotel_Management';
};

const renderFieldTable = (packageField: string, hotelField: string) => {
  return tabType === 'PACKAGE' ? packageField : hotelField;
};

const sortMessage = () => {
  isSort.value = !isSort.value;
  sortOrder.value = isSort.value ? -1 : 0;
};

const renderGroupTypes = (groupTypes: any) => {
  return groupTypes?.join(',');
};

onBeforeMount(() => {
  hotels.map((hotel: any) => {
    hotel.groupTypes = renderGroupTypes(hotel.groupTypes);
    hotel.groupTypesHotel = renderGroupTypes(hotel.groupTypesHotel);
  });

  sheetsDataHotel.value = [
    {
      name: 'Sheet 1',
      data: sheetData
    }
  ];
});
</script>
<template>
  <div>
    <div class="view-hotel-modal">
      <DataTable v-model:filters="filters" :value="hotels" :rowClass="rowClass" paginator showGridlines class="text-sm" :rows="10" :sortOrder="sortOrder" :sortField="sortField" dataKey="id" data-testid="table_hotel" filterDisplay="menu">
        <template #empty> {{ $t('management.no_data_found') }} </template>
        <template #loading> {{ $t('common.generic.loading') }}. {{ $t('my_quote.please_wait') }}. </template>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="hCode" :header="$t('management.hotel.tts_id')" class="m-w-100 white-space-nowrap">
          <template #body="{ data }">
            {{ data.hCode }}
          </template>
          <template #filter="{ filterModel }">
            <InputText data-testid="filter_hCode" v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_tts_id')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="code" class="m-w-128">
          <template #header>
            <span class="text-center"> {{ $t('management.hotel.sv_id') }}</span>
          </template>
          <template #body="{ data }">
            {{ data.code }}
          </template>
          <template #filter="{ filterModel }">
            <InputText id="sv_code" v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_soft_voyage_id')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="name" :header="`${$t('management.hotel.hotel_name')} (EN)`" class="min-w-240">
          <template #body="{ data }">
            <div>{{ data.name }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_hotel_name')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="nameFr" :header="`${$t('management.hotel.hotel_name')} (FR)`" class="min-w-240">
          <template #body="{ data }">
            <div>{{ data.nameFr }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_hotel_name')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="chainName" :header="`${$t('management.hotel.chain_name')} (EN)`">
          <template #body="{ data }">
            <div>{{ data.chainName }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_chain_name')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="chainNameFr" :header="`${$t('management.hotel.chain_name')} (FR)`">
          <template #body="{ data }">
            <div>{{ data.chainNameFr }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_chain_name')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="destSVId" :header="$t('management.package.dest_sv_id')">
          <template #body="{ data }">
            <div>{{ data.destSVId }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="destName" :header="`${$t('common.destination')} (EN)`">
          <template #body="{ data }">
            <div>{{ data.destName }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_destination')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="destNameFr" :header="`${$t('common.destination')} (FR)`">
          <template #body="{ data }">
            <div>{{ data.destNameFr }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_destination')" />
          </template>
        </TableColumn>
        <TableColumn :header="$t('management.hotel.max_rooms')" :field="renderFieldTable('maxRooms', 'maxRoomsHotel')" class="m-w-132">
          <template #body="{ data, field }">
            <div>{{ data[field] }}</div>
          </template>
        </TableColumn>
        <TableColumn :header="$t('management.hotel.quote_expiry_restriction')" field="quoteExpiryRestriction" class="m-w-132">
          <template #body="{ data, field }">
            <div>{{ data[field] }}</div>
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" :field="renderFieldTable('excludeRoomCategoryPackage', 'excludeRoomCategoryHotel')" :header="$t('management.hotel.exclude_room')">
          <template #body="{ data, field }">
            <div class="flex align-items-center min-w-240">
              <ViewRoomExclusions :excludeRooms="data[field]" :maxCharacters="165" />
            </div>
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" :field="renderFieldTable('groupTypes', 'groupTypesHotel')" :header="$t('management.hotel.group_type')">
          <template #body="{ data, field }">
            <div>
              {{ data[field] }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" class="min-w-200" :show-add-button="false" :field="renderFieldTable('roomCategoryCallOrder', 'roomCategoryCallOrderHotel')" :header="$t('management.hotel.room_category_call_order')">
          <template #body="{ data, field }">
            <div>
              {{ data[field] }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <Select v-model="filterModel.value" :options="apiOrder" optionLabel="label" optionValue="value" :placeholder="$t('common.select_one')" class="p-column-filter table-select w-full" showClear>
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  <div class="w-6rem overflow-hidden text-overflow-ellipsis">{{ apiOrder.find((type) => type.value === slotProps.value)?.label }}</div>
                </div>
              </template>
            </Select>
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" :field="renderFieldTable('hotelPreferred.sts', 'hotelPreferredHotel.sts')" :header="$t('management.hotel.preferred')" class="m-w-128">
          <template #body="{ data, field }">
            {{ renderStatus(data[field]?.sts) }}
            <div>
              {{ formatDate(data[field]?.startDate, 'dd-LLL-yyyy', false, currentLanguage) }}
              {{ formatDate(data[field]?.endDate, 'dd-LLL-yyyy', false, currentLanguage) }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
              <template #option="slotProps">
                <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
              </template>
            </Select>
          </template>
        </TableColumn>
        <TableColumn class="min-w-200" :field="renderFieldTable('bookingDate', 'bookingDateHotel')" :header="$t('hotel_contract.booking_date_range')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false">
          <template #body="{ data, field }">
            <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn class="min-w-200" :field="renderFieldTable('travelDate', 'travelDateHotel')" :header="$t('hotel_contract.travel_date_range')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false">
          <template #body="{ data, field }">
            <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn class="min-w-200" :field="renderFieldTable('blackoutDate', 'blackoutDateHotel')" :header="$t('hotel_contract.black_out_date')" :showAddButton="false" :showFilterMatchModes="false" :showFilterOperator="false">
          <template #body="{ data, field }">
            <p class="mb-0 white-space">{{ handleDisplayDate(data[field]) }}</p>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" :field="renderFieldTable('insta', 'instaHotel')" :header="$t('management.hotel.instaquote')" class="m-w-128">
          <template #body="{ data, field }">
            <div>
              {{ renderStatus(data[field]) }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
              <template #option="slotProps">
                <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
              </template>
            </Select>
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" :field="renderFieldTable('hotelType', 'hotelTypeHotel')" :header="$t('management.hotel.hotel_type')">
          <template #body="{ data, field }">
            <div>{{ data[field] }}</div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" :field="renderFieldTable('status', 'statusHotel')" :header="$t('common.status')">
          <template #body="{ data, field }">
            <div class="text-center">
              {{ renderStatus(data[field]) }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
              <template #option="slotProps">
                <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
              </template>
            </Select>
          </template>
        </TableColumn>
        <TableColumn class="min-w-200" field="validateMessage" :header="$t('management.hotel.message')" :showAddButton="false" :showFilterOperator="false" sortable>
          <template #sorticon>
            <i class="ml-2 sort_icon" :class="sortOrder === -1 ? 'pi pi-sort-amount-down-alt' : 'pi pi-sort-amount-up-alt'" @click="sortMessage"></i>
          </template>
          <template #body="{ data, field }">
            <p class="mb-0 break-spaces">{{ handleShowMessage(data[field]) }}</p>
          </template>
        </TableColumn>
      </DataTable>
      <div class="flex">
        <div class="w-full text-center" v-if="type === 'IMPORT'">
          <Button id="btn_import_file" icon="pi pi-plus" @click="handleImportFile()" :label="$t('common.buttons.import_file_now')" class="import-hotel-btn ml-4" />
        </div>
        <div class="w-full text-center" v-if="type === 'REVERT'">
          <Button id="btn_revert_file" icon="pi pi-plus" @click="handleRevert()" :label="$t('common.labels.revert_to_this_version')" class="revert-hotel-btn ml-4" />
        </div>
        <div class="w-full text-center" v-if="type === 'EXPORT'">
          <ExportExcelFile id="export_btn" class="export ml-4" :sheets="sheetsDataHotel" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport(renderName())"></ExportExcelFile>
        </div>
        <div>
          <Button id="btn_close" :label="$t('common.buttons.close')" class="p-button" @click="closeModal()" />
        </div>
      </div>
    </div>
  </div>
</template>
